import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useUnmount } from 'react-use';
import VideoJsPlayer from 'video.js/dist/types/player';
import videojs from 'utils/videojsWrapper';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import classNames from 'classnames';

import styles from './VideoPlaylistPlayer.module.scss';

interface VideoPlayerContentProps {
  id: string;
  src: string | null;
  visible?: boolean;
  onEnded?: () => void;
  shouldAutoPlay?: boolean;
  adRunning?: boolean;
  onAutoplayBlocked?: () => void;
}

export interface VideoPlayerHandle {
  toggleNativeFullscreen: () => void;
  getPlayer: () => VideoJsPlayer | null;
  getCurrentTimestamp: () => number | null;
  isPlaying: () => boolean;
  play: () => void;
  pause: () => void;
}

const VideoPlayerContent = forwardRef<VideoPlayerHandle, VideoPlayerContentProps>(
  (
    {
      id,
      src,
      visible,
      onEnded = () => {},
      shouldAutoPlay = true,
      adRunning = false,
      onAutoplayBlocked,
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<VideoJsPlayer | null>(null);
    const wasTabHiddenRef = useRef(false);

    useImperativeHandle(
      ref,
      () => ({
        toggleNativeFullscreen() {
          const player = playerRef.current;
          if (player?.isFullscreen?.()) {
            player.exitFullscreen();
          } else {
            player?.requestFullscreen();
          }
        },
        getPlayer() {
          return playerRef.current;
        },
        getCurrentTimestamp() {
          return playerRef.current?.currentTime() ?? null;
        },
        isPlaying() {
          const player = playerRef.current;
          return player?.paused() === false;
        },
        isPaused() {
          const player = playerRef.current;
          return player?.paused() === true;
        },
        play() {
          playerRef.current?.play();
        },
        pause() {
          playerRef.current?.pause();
        },
      }),
      [],
    );

    useEffect(() => {
      if (videoRef.current) {
        let player: VideoJsPlayer;
        if (!playerRef.current) {
          const videoElement = window.document.createElement('video-js');
          videoElement.setAttribute('id', id);
          videoRef.current.appendChild(videoElement);

          player = videojs(videoElement, {
            aspectRatio: '16:9',
            autoplay: shouldAutoPlay,
            controls: false,
            disablePictureInPicture: true,
            muted: false,
            preload: 'auto',
            sources: [{ src, type: 'video/mp4' }],
            playsinline: true,
          });
          player.addClass('vjs-big-play-centered vjs-sl-cam-player');
          playerRef.current = player;
        } else {
          player = playerRef.current;
          player.src([{ src, type: 'video/mp4' }]);
        }
        player.on('ended', onEnded);
        player.addClass('vjs-sl-cam-player-none');

        return () => {
          player.off('ended', onEnded);
        };
      }

      return () => {};
    }, [id, src, onEnded, shouldAutoPlay]);

    useUnmount(() => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        try {
          player.dispose();
          playerRef.current = null;
        } catch (error) {
          console?.error(error);
        }
      }
    });

    // Firefox fix: Reload source when tab becomes visible to prevent decoder errors
    const handleVisibilityChange = useCallback(() => {
      if (document.hidden) {
        wasTabHiddenRef.current = true;
      } else if (wasTabHiddenRef.current && visible && src) {
        // Tab became visible and this is the visible player - reload source
        const player = playerRef.current;
        if (player && !player.isDisposed()) {
          player.src([{ src, type: 'video/mp4' }]);
          player.load();
        }
        wasTabHiddenRef.current = false;
      }
    }, [visible, src]);

    useEffect(() => {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [handleVisibilityChange]);

    useEffect(() => {
      const player = playerRef.current;
      if (player) {
        if (visible && shouldAutoPlay && !adRunning) {
          // Surface autoplay-policy rejections so the parent can re-show the play overlay for a fresh gesture.
          const result = player?.play();
          if (result && typeof result.catch === 'function') {
            result.catch((err: unknown) => {
              const name = (err as { name?: string } | null)?.name;
              if (name === 'NotAllowedError') {
                onAutoplayBlocked?.();
              }
            });
          }
        } else {
          player?.pause();
          if (!visible) {
            player?.currentTime(0);
          }
        }
      }
    }, [visible, shouldAutoPlay, adRunning, onAutoplayBlocked]);

    return (
      <div
        className={classNames(styles.VideoPlayer, {
          [styles.hide]: !visible,
        })}
        ref={videoRef}
      />
    );
  },
);

VideoPlayerContent.displayName = 'VideoPlayerContent';

const VideoPlayerContentMemo = React.memo(VideoPlayerContent);

export interface VideoPlayerProps {
  shouldAutoPlay?: boolean;
  adRunning?: boolean;
  error?: { status: number; name: string; message: string };
  playlist: (string | null)[];
  currentPlayer: number;
  setCurrentPlayer: (index: number) => void;
  setShowEndOfReelsCTA?: () => void;
  onAutoplayBlocked?: () => void;
}

export const VideoPlaylistPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(
  (
    {
      shouldAutoPlay = true,
      adRunning = false,
      playlist,
      error,
      currentPlayer,
      setCurrentPlayer,
      setShowEndOfReelsCTA,
      onAutoplayBlocked,
    },
    ref,
  ) => {
    const playerRefs = useRef<(VideoPlayerHandle | null)[]>([]);

    // Use refs to capture latest values without recreating callbacks
    const currentPlayerRef = useRef(currentPlayer);
    const playlistRef = useRef(playlist);
    const setCurrentPlayerRef = useRef(setCurrentPlayer);
    const setShowEndOfReelsCTARef = useRef(setShowEndOfReelsCTA);

    useEffect(() => {
      currentPlayerRef.current = currentPlayer;
      playlistRef.current = playlist;
      setCurrentPlayerRef.current = setCurrentPlayer;
      setShowEndOfReelsCTARef.current = setShowEndOfReelsCTA;
    });

    useImperativeHandle(
      ref,
      () => ({
        toggleNativeFullscreen() {
          playerRefs.current[currentPlayer]?.toggleNativeFullscreen();
        },
        getPlayer() {
          return playerRefs.current[currentPlayer]?.getPlayer() ?? null;
        },
        isPlaying() {
          return playerRefs.current[currentPlayer]?.isPlaying() ?? false;
        },
        play() {
          playerRefs.current[currentPlayer]?.play();
        },
        pause() {
          playerRefs.current[currentPlayer]?.pause();
        },
        getCurrentTimestamp() {
          return playerRefs.current[currentPlayer]?.getCurrentTimestamp() ?? null;
        },
      }),
      [currentPlayer],
    );

    // Create stable onEnded callbacks for each video index (only when playlist changes)
    const onEndedCallbacks = useMemo(() => {
      return playlist.map(() => () => {
        if (currentPlayerRef.current < playlistRef.current.length - 1) {
          setCurrentPlayerRef.current(currentPlayerRef.current + 1);
        } else if (
          currentPlayerRef.current === playlistRef.current.length - 1 &&
          setShowEndOfReelsCTARef.current
        ) {
          setShowEndOfReelsCTARef.current();
        }
      });
    }, [playlist]);

    return (
      <ErrorBoundary
        error={error}
        errorBoundaryId="video-playlist-player"
        render={() => <ErrorMessage />}
      >
        {playlist.map((src, index) => (
          <VideoPlayerContentMemo
            ref={(el) => {
              playerRefs.current[index] = el;
            }}
            onEnded={onEndedCallbacks[index]}
            id={`video-playlist-player-${src}-${index}`}
            visible={currentPlayer === index}
            shouldAutoPlay={shouldAutoPlay}
            adRunning={adRunning}
            onAutoplayBlocked={onAutoplayBlocked}
            src={src}
            key={`${src}-${index}`}
          />
        ))}
      </ErrorBoundary>
    );
  },
);

VideoPlaylistPlayer.displayName = 'VideoPlaylistPlayer';

export default VideoPlaylistPlayer;
